import { useRef, useEffect, useState, useMemo } from 'react';
import { flushSync } from 'react-dom';
import Lottie from 'lottie-react';
import StonePhone3D from '../components/StonePhone3D';
import { Link } from 'react-router-dom';

// Import Lottie animations
import moreLifeAnimation from '../assets/animations/more_life.json';
import longTermAnimation from '../assets/animations/long_term.json';
import functionOverFrictionAnimation from '../assets/animations/function_over_friction.json';
import stonesAnimation from '../assets/animations/stones.json';

// ============================================================================
// CONSTANTS
// ============================================================================

// Colors
const COLORS = {
  white: '#ffffff',
  black: '#000000',
  blue: '#799fb5',
  blueBackground: '#10254a',
} as const;

// Typography
const TYPOGRAPHY = {
  fontFamily: 'monospace',
  fontSize: {
    small: '14px',
    medium: '16px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
  },
} as const;

// Layout
const LAYOUT = {
  spacing: {
    small: '20px',
    medium: '40px',
  },
  zIndex: {
    base: 100,
    navigation: 150,
  },
  footer: {
    heightVh: 15,
  },
} as const;

// Animations
const ANIMATION_DURATIONS = {
  default: 800,
  fast: 150,
  slow: 1200, // Doubled speed for Stone responses (was 2400)
  verySlow: 3200,
  userResponse: 500,
} as const;

// Cursor rotation settings for Frame 1
const CURSOR_ROTATION = {
  enabled: true, // Set to false to disable cursor-based rotation
  sensitivity: 3.0, // How much the phone rotates (0.01 = subtle, 0.1 = dramatic)
  maxRotation: 270, // Maximum rotation in degrees (allows seeing the back)
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Easing function
const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// Arrow SVG component
const ArrowSVG = ({ rotation = 0, stroke = COLORS.white }: { rotation?: number; stroke?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: `rotate(${rotation}deg)` }}
  >
    <path
      d="M8 3 L8 13 M8 13 L4 9 M8 13 L12 9"
      stroke={stroke}
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Navigation button base styles
const navButtonBaseStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: LAYOUT.spacing.small,
  zIndex: LAYOUT.zIndex.base,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  fontFamily: TYPOGRAPHY.fontFamily,
  fontSize: TYPOGRAPHY.fontSize.small,
  color: COLORS.white,
  transition: 'color 0.3s ease',
};

// Hover handlers (desktop only - check for touch device)
const isTouchDevice = () => {
  return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
};

const createHoverHandlers = (shouldApplyBlueHover: boolean = true) => ({
  onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
    if (shouldApplyBlueHover && !isTouchDevice()) {
      e.currentTarget.style.color = COLORS.blue;
      const svg = e.currentTarget.querySelector('svg path');
      if (svg) svg.setAttribute('stroke', COLORS.blue);
    }
  },
  onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isTouchDevice()) {
      e.currentTarget.style.color = COLORS.white;
      const svg = e.currentTarget.querySelector('svg path');
      if (svg) svg.setAttribute('stroke', COLORS.white);
    }
  },
});

// ============================================================================
// COMPONENT
// ============================================================================

export default function MainPage() {
  const [currentSection, setCurrentSection] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAccordionAnimating, setIsAccordionAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [skipAccordionTransition, setSkipAccordionTransition] = useState(false);
  const [presetAccordionSection, setPresetAccordionSection] = useState<number | null>(null);
  const [navHovered, setNavHovered] = useState(false);
  const [hoveredNavDot, setHoveredNavDot] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Cursor-based rotation for Frame 1
  const [cursorX, setCursorX] = useState(0.5); // Normalized 0-1 (0.5 = center)
  const [cursorY, setCursorY] = useState(0.5); // Normalized 0-1 (0.5 = center)

  // Memoize cursor rotation settings to prevent unnecessary re-renders
  const memoizedCursorSettings = useMemo(() => CURSOR_ROTATION, []);

  // Cycling adjectives for Frame 1
  const adjectives = ['different', 'better', 'new', 'authentic', 'real', 'simple', 'focused', 'intentional', 'mindful', 'human', 'intelligent'];
  const [currentAdjectiveIndex, setCurrentAdjectiveIndex] = useState(0);
  const [prevAdjectiveIndex, setPrevAdjectiveIndex] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Auto-rotation angle for mobile (0 to 1)
  const [autoRotationAngle, setAutoRotationAngle] = useState(0);

  // Frame 1 & 2: Rock animations
  const [frame1TextProgress, setFrame1TextProgress] = useState(0);
  const [frame2TextProgress, setFrame2TextProgress] = useState(0);
  const [rockProgress, setRockProgress] = useState(0);

  // Frame 3: Logo scale and typewriter
  const [frame3Progress, setFrame3Progress] = useState(0);
  const [typewriterProgress, setTypewriterProgress] = useState(0);
  const [greetingTypewriter, setGreetingTypewriter] = useState(0);

  // Frame 3a → 3b → 3c → 3d → 3e → 3f → 3g → 3h (Chat sequence)
  const [section3Progress, setSection3Progress] = useState(0); // 3a→3b: Logo moves down
  const [section4Progress, setSection4Progress] = useState(0); // 3b→3c: User1 types
  const [section5Progress, setSection5Progress] = useState(0); // 3c→3d: Stone1 types
  const [section6Progress, setSection6Progress] = useState(0); // 3d→3e: User2 types
  const [section7Progress, setSection7Progress] = useState(0); // 3e→3f: Stone2 types
  const [section8Progress, setSection8Progress] = useState(0); // 3f→3g: User3 types
  const [section9Progress, setSection9Progress] = useState(0); // 3g→3h: Stone3 types
  const [demoToInterfaceProgress, setDemoToInterfaceProgress] = useState(0); // 3h→Interface: Slide to INTERFACE

  // Frame 3 → 4 transitions (Blue swipe up)
  const [section10Progress, setSection10Progress] = useState(0); // 3a→4a: Blue swipe from 3a
  const [section11Progress, setSection11Progress] = useState(0); // 3h→4a: Blue swipe from 3h

  // Frame 4a → 4b → 4c (Sliding content)
  const [section12Progress, setSection12Progress] = useState(0); // 4a→4b
  const [section13Progress, setSection13Progress] = useState(0); // 4b→4c

  // Frame 4c → 5 (Black frame slide up)
  const [section14Progress, setSection14Progress] = useState(0); // 4c→5

  // Frame 5 → Footer reveal
  const [section15Progress, setSection15Progress] = useState(0); // 5→Footer

  // Phone visibility control (to prevent glitch during instant navigation)
  const [hidePhone, setHidePhone] = useState(false);

  const [chatEnabled, setChatEnabled] = useState(false);
  const [scrollIndicatorOpacity, setScrollIndicatorOpacity] = useState(1);
  const [displaySection, setDisplaySection] = useState(0); // Section to display buttons for (updates immediately on transition start)
  const [hoveredApp, setHoveredApp] = useState<string | null>(null); // For UI showcase frame
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 }); // For tooltip positioning

  // Animation timing helpers for chat sections
  const sec4Anim1 = section4Progress; // User1 typewriter (100%)
  const sec5Anim1 = section5Progress; // Stone1 typewriter (100%)
  const sec6Anim1 = section6Progress; // User2 typewriter (100%)
  const sec7Anim1 = section7Progress; // Stone2 typewriter (100%)
  const sec8Anim1 = section8Progress; // User3 typewriter (100%)
  const sec9Anim1 = section9Progress; // Stone3 typewriter (100%)

  // Blue swipe animations
  const sec10Anim1 = section10Progress; // Blue swipe from 3a (100%)
  const sec11Anim1 = section11Progress; // Blue swipe from 3h (100%)

  // UI Showcase apps data
  const apps = [
    { name: 'tick', path: '/tick', description: 'Set alarms, timers, world clocks—all through voice' },
    { name: 'pebbles', path: '/pebbles', description: 'Add any third-party service—Uber, Airbnb, banking, automotive, anything from the app store' },
    { name: 'set', path: '/set', description: 'Change any setting just by asking' },
    { name: 'listen', path: '/listen', description: 'Voice-controlled music—play, skip, discover' },
    { name: 'ask', path: '/ask', description: 'Instant answers with up-to-date web information' },
    { name: 'look', path: '/look', description: 'Books and movies—smart recommendations, track progress' },
    { name: 'plan', path: '/plan', description: 'Calendar and goals—schedule, track milestones' },
    { name: 'think', path: '/think', description: 'Voice notes, auto-organized and searchable' },
    { name: 'reflect', path: '/reflect', description: 'Automatic life log—searchable daily timeline' },
    { name: 'connect', path: '/connect', description: 'Messages, emails, calls—all platforms in one place' },
    { name: 'go', path: '/go', description: 'Navigate anywhere—directions, discovery, real-time traffic' },
    { name: 'fund', path: '/fund', description: 'Cards, tickets, payments—secure and simple' }
  ];

  // User text 1 - "Catch me up."
  const userText1 = "Catch me up.";
  const user1Chars = Math.floor(userText1.length * sec4Anim1);
  const user1Visible = userText1.slice(0, user1Chars);
  const user1Cursor = sec4Anim1 > 0 && sec4Anim1 < 1;

  // Stone response 1 - Split into three parts
  const stoneText1Part1 = "You've got 2 messages from Tim.";
  const stoneText1Part2 = "Want me to tell him";
  const stoneText1Part3 = `Hey, I'm at the trail right now. I'll take a look this afternoon.`;

  // Calculate visibility for all parts
  const stoneText1Full = stoneText1Part1 + '\n' + stoneText1Part2 + '"' + stoneText1Part3 + '"';
  const stone1Chars = Math.floor(stoneText1Full.length * sec5Anim1);
  const stone1Visible = stoneText1Full.slice(0, stone1Chars);
  const stone1Cursor = sec5Anim1 > 0 && sec5Anim1 < 1;

  // Split visibility across three parts
  const stone1Part1Visible = stone1Visible.slice(0, Math.min(stone1Visible.length, stoneText1Part1.length));
  const stone1Part2Start = stoneText1Part1.length + 1;
  const stone1Part2End = stone1Part2Start + stoneText1Part2.length; // Just part 2 text, no quote
  const stone1Part2Visible = stone1Visible.length > stone1Part2Start
    ? stone1Visible.slice(stone1Part2Start, Math.min(stone1Visible.length, stone1Part2End))
    : '';
  const stone1Part3Start = stone1Part2End + 1; // +1 to skip the opening quote before part 3
  const stone1Part3Visible = stone1Visible.length > stone1Part3Start
    ? stone1Visible.slice(stone1Part3Start, Math.min(stone1Visible.length, stone1Part3Start + stoneText1Part3.length))
    : '';

  // Determine cursor position
  const stone1CursorOnPart1 = stone1Cursor && stone1Visible.length <= stoneText1Part1.length;
  const stone1CursorOnPart2 = stone1Cursor && stone1Visible.length > stone1Part2Start && stone1Visible.length <= stone1Part2End;
  const stone1CursorOnPart3 = stone1Cursor && stone1Visible.length > stone1Part3Start;

  // User text 2 - "Yes, sounds good."
  const userText2 = "Yes, sounds good.";
  const user2Chars = Math.floor(userText2.length * sec6Anim1);
  const user2Visible = userText2.slice(0, user2Chars);
  const user2Cursor = sec6Anim1 > 0 && sec6Anim1 < 1;

  // Stone response 2 - Split into parts for tool call display
  const stoneText2Part1 = "I noticed that Friday is the anniversary of when you and Danielle moved in together.";
  const stoneText2Part2 = "I also saw that you bookmarked Fiorella.";
  const stoneText2Part3 = "They have availability for 6:30.";
  const stoneText2Part4 = "Would you like me to book that for you?";

  // Calculate visibility for Stone message 2 parts
  const stoneText2Full = stoneText2Part1 + '\n' + stoneText2Part2 + '\n' + stoneText2Part3 + '\n' + stoneText2Part4;
  const stone2Chars = Math.floor(stoneText2Full.length * sec7Anim1);
  const stone2Visible = stoneText2Full.slice(0, stone2Chars);
  const stone2Cursor = sec7Anim1 > 0 && sec7Anim1 < 1;

  // Split visibility across four parts
  const stone2Part1Visible = stone2Visible.slice(0, Math.min(stone2Visible.length, stoneText2Part1.length));
  const stone2Part2Start = stoneText2Part1.length + 1;
  const stone2Part2End = stone2Part2Start + stoneText2Part2.length;
  const stone2Part2Visible = stone2Visible.length > stone2Part2Start
    ? stone2Visible.slice(stone2Part2Start, Math.min(stone2Visible.length, stone2Part2End))
    : '';
  const stone2Part3Start = stone2Part2End + 1;
  const stone2Part3End = stone2Part3Start + stoneText2Part3.length;
  const stone2Part3Visible = stone2Visible.length > stone2Part3Start
    ? stone2Visible.slice(stone2Part3Start, Math.min(stone2Visible.length, stone2Part3End))
    : '';
  const stone2Part4Start = stone2Part3End + 1;
  const stone2Part4Visible = stone2Visible.length > stone2Part4Start
    ? stone2Visible.slice(stone2Part4Start, Math.min(stone2Visible.length, stone2Part4Start + stoneText2Part4.length))
    : '';

  // Determine cursor position for Stone message 2
  const stone2CursorOnPart1 = stone2Cursor && stone2Visible.length <= stoneText2Part1.length;
  const stone2CursorOnPart2 = stone2Cursor && stone2Visible.length > stone2Part2Start && stone2Visible.length <= stone2Part2End;
  const stone2CursorOnPart3 = stone2Cursor && stone2Visible.length > stone2Part3Start && stone2Visible.length <= stone2Part3End;
  const stone2CursorOnPart4 = stone2Cursor && stone2Visible.length > stone2Part4Start;

  // User text 3 - "Perfect, thanks."
  const userText3 = "Perfect, thanks.";
  const user3Chars = Math.floor(userText3.length * sec8Anim1);
  const user3Visible = userText3.slice(0, user3Chars);
  const user3Cursor = sec8Anim1 > 0 && sec8Anim1 < 1;

  // Stone response 3 - Final message
  const stoneText3Part1 = "No problem.";
  const stoneText3Part2 = "You can put me down and get back to your hike now.";

  // Calculate visibility for Stone message 3 parts
  const stoneText3Full = stoneText3Part1 + '\n' + stoneText3Part2;
  const stone3Chars = Math.floor(stoneText3Full.length * sec9Anim1);
  const stone3Visible = stoneText3Full.slice(0, stone3Chars);
  const stone3Cursor = sec9Anim1 > 0 && sec9Anim1 < 1;

  // Split visibility across two parts
  const stone3Part1Visible = stone3Visible.slice(0, Math.min(stone3Visible.length, stoneText3Part1.length));
  const stone3Part2Start = stoneText3Part1.length + 1;
  const stone3Part2Visible = stone3Visible.length > stone3Part2Start
    ? stone3Visible.slice(stone3Part2Start, Math.min(stone3Visible.length, stone3Part2Start + stoneText3Part2.length))
    : '';

  // Determine cursor position for Stone message 3
  const stone3CursorOnPart1 = stone3Cursor && stone3Visible.length <= stoneText3Part1.length;
  const stone3CursorOnPart2 = stone3Cursor && stone3Visible.length > stone3Part2Start;

  // Helper function to reset all transition progress states
  const resetAllTransitionProgress = () => {
    setSection3Progress(0);
    setSection4Progress(0);
    setSection5Progress(0);
    setSection6Progress(0);
    setSection7Progress(0);
    setSection8Progress(0);
    setSection9Progress(0);
    setDemoToInterfaceProgress(0);
    setSection10Progress(0);
    setSection11Progress(0);
    setSection12Progress(0);
    setSection13Progress(0);
    setSection14Progress(0);
    setSection15Progress(0);
  };

  const animateToSection = (targetSec: number) => {
    if (isAnimating || targetSec === currentSection) return;

    setIsAnimating(true);
    setDisplaySection(targetSec); // Update button display immediately
    const startSection = currentSection;

    // For Frame 4 accordion transitions, set accordion animating flag immediately
    const isFrame4Transition = (startSection >= 10 && startSection <= 12) && (targetSec >= 10 && targetSec <= 12);
    if (isFrame4Transition) {
      setIsAccordionAnimating(true);
    }

    // Preset accordion state without triggering Frame 4 render yet
    if ((startSection === 2 && targetSec === 9) || (startSection === 8 && targetSec === 9) || (startSection === 12 && targetSec === 11)) {
      setPresetAccordionSection(targetSec);
    }

    // WHY → APPROACH: preset accordion to section 3 (expanded) at start of animation
    if (startSection === 13 && targetSec === 12) {
      setPresetAccordionSection(12);
      setSkipAccordionTransition(true);
    }

    // For Frame 4 transitions: skip animation entirely, just lock scroll during CSS transition
    if (isFrame4Transition) {
      setCurrentSection(targetSec);
      // Lock scroll for 1000ms while accordion CSS transition plays
      setTimeout(() => {
        setIsAnimating(false);
        setIsAccordionAnimating(false);
      }, 1000);
      return; // Exit early, no animation needed
    }

    // Duration logic for other transitions
    let duration: number = ANIMATION_DURATIONS.default;
    // Frame 3a→3b (section 2→3): Default duration with logo movement
    if ((startSection === 2 && targetSec === 3) || (startSection === 3 && targetSec === 2)) {
      duration = ANIMATION_DURATIONS.default;
    }
    // Frame 3b→3c (section 3→4): Slower for Stone response
    else if ((startSection === 3 && targetSec === 4) || (startSection === 4 && targetSec === 3)) {
      duration = ANIMATION_DURATIONS.slow;
    }
    // Fast duration for User messages (sections 5→6, 7→8)
    else if ((startSection === 4 && targetSec === 5) || (startSection === 5 && targetSec === 4) ||
             (startSection === 6 && targetSec === 7) || (startSection === 7 && targetSec === 6)) {
      duration = ANIMATION_DURATIONS.userResponse;
    }
    // Medium duration for Stone responses (changed from verySlow to slow for faster typing)
    else if ((startSection >= 3 && startSection <= 8) && (targetSec >= 3 && targetSec <= 8)) {
      duration = ANIMATION_DURATIONS.slow;
    }

    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);

      // Sections 0-2 (Frame 1, 2, 3)
      if (startSection === 0 && targetSec === 1) {
        setFrame1TextProgress(easedProgress);
        setFrame2TextProgress(easedProgress);
        setRockProgress(easedProgress);
        // Fade scroll indicator out during forward transition (1 → 0)
        setScrollIndicatorOpacity(1 - easedProgress);
      }
      else if (startSection === 1 && targetSec === 0) {
        setFrame1TextProgress(1 - easedProgress);
        setFrame2TextProgress(1 - easedProgress);
        setRockProgress(1 - easedProgress);
        // Fade scroll indicator back in during reverse transition (0 → 1)
        setScrollIndicatorOpacity(easedProgress);
      }
      else if (startSection === 1 && targetSec === 2) {
        setFrame3Progress(easedProgress);
        setTypewriterProgress(easedProgress);
      }
      else if (startSection === 2 && targetSec === 1) {
        setFrame3Progress(1 - easedProgress);
        setFrame2TextProgress(1);
        if (easedProgress < 0.5) {
          setTypewriterProgress(1 - (easedProgress * 2));
        } else {
          setTypewriterProgress(0);
        }
      }
      // Section 3 (3a→3b): Logo moves down AND User1 message types (combined old 3b+3c)
      else if (startSection === 2 && targetSec === 3) {
        setSection3Progress(easedProgress);
        setSection4Progress(easedProgress); // User message types at same time
      }
      else if (startSection === 3 && targetSec === 2) {
        setSection3Progress(1 - easedProgress);
        setSection4Progress(1 - easedProgress);
        // Reset all subsequent chat messages when going back to 3a
        setSection5Progress(0);
        setSection6Progress(0);
        setSection7Progress(0);
      }
      // Section 4 (3b→3c): AI1 types (was old 3c→3d)
      else if (startSection === 3 && targetSec === 4) {
        setSection5Progress(easedProgress);
      }
      else if (startSection === 4 && targetSec === 3) {
        setSection5Progress(1 - easedProgress);
        // Reset subsequent messages
        setSection6Progress(0);
        setSection7Progress(0);
      }
      else if (startSection === 4 && targetSec === 2) {
        // Going from 3c directly to 3a - animate chat progress backwards
        setSection3Progress(1 - easedProgress);
        setSection4Progress(0);
        setSection5Progress(0);
        setSection6Progress(0);
        setSection7Progress(0);
      }
      // Section 5 (3c→3d): User2 types (was old 3d→3e)
      else if (startSection === 4 && targetSec === 5) {
        setSection6Progress(easedProgress);
      }
      else if (startSection === 5 && targetSec === 4) {
        setSection6Progress(1 - easedProgress);
        // Reset subsequent message
        setSection7Progress(0);
      }
      else if (startSection === 5 && targetSec === 2) {
        // Going from 3d directly to 3a - animate chat progress backwards
        setSection3Progress(1 - easedProgress);
        setSection4Progress(0);
        setSection5Progress(0);
        setSection6Progress(0);
        setSection7Progress(0);
      }
      // Section 6 (3d→3e): Stone2 types
      else if (startSection === 5 && targetSec === 6) {
        setSection7Progress(easedProgress);
      }
      else if (startSection === 6 && targetSec === 5) {
        setSection7Progress(1 - easedProgress);
        // Reset subsequent messages
        setSection8Progress(0);
        setSection9Progress(0);
      }
      else if (startSection === 6 && targetSec === 2) {
        // Going from 3e directly to 3a - animate chat progress backwards
        setSection3Progress(1 - easedProgress);
        setSection4Progress(0);
        setSection5Progress(0);
        setSection6Progress(0);
        setSection7Progress(0);
        setSection8Progress(0);
        setSection9Progress(0);
      }
      // Section 7 (3e→3f): User3 types
      else if (startSection === 6 && targetSec === 7) {
        setSection8Progress(easedProgress);
      }
      else if (startSection === 7 && targetSec === 6) {
        setSection8Progress(1 - easedProgress);
        // Reset subsequent message
        setSection9Progress(0);
      }
      else if (startSection === 7 && targetSec === 2) {
        // Going from 3f directly to 3a - animate chat progress backwards
        setSection3Progress(1 - easedProgress);
        setSection4Progress(0);
        setSection5Progress(0);
        setSection6Progress(0);
        setSection7Progress(0);
        setSection8Progress(0);
        setSection9Progress(0);
      }
      // Section 8 (3f→3g): Stone3 types
      else if (startSection === 7 && targetSec === 8) {
        setSection9Progress(easedProgress);
      }
      else if (startSection === 8 && targetSec === 7) {
        setSection9Progress(1 - easedProgress);
        // Reset demoToInterface progress when going back
        setDemoToInterfaceProgress(0);
      }
      else if (startSection === 8 && targetSec === 2) {
        // Going from 3g directly to 3a - animate chat progress backwards
        setSection3Progress(1 - easedProgress);
        setSection4Progress(0);
        setSection5Progress(0);
        setSection6Progress(0);
        setSection7Progress(0);
        setSection8Progress(0);
        setSection9Progress(0);
      }
      // Section 10 (3a→4a): Blue swipe from Frame 3a to APPROACH
      else if (startSection === 2 && targetSec === 10) {
        setSection10Progress(easedProgress);
        // Skip accordion transition when entering 4a from 3a
        if (progress === 0) {
          setSkipAccordionTransition(true);
        }
      }
      else if (startSection === 10 && targetSec === 2) {
        setSection10Progress(1 - easedProgress);
        // Reset all chat progress states to return to clean Frame 3a
        setSection3Progress(0);
        setSection4Progress(0);
        setSection5Progress(0);
        setSection6Progress(0);
        setSection7Progress(0);
        setSection8Progress(0);
        setSection9Progress(0);
        setSection11Progress(0);
      }
      // Section 11 (3h→UI showcase): Transition from Frame 3h to UI showcase
      else if (startSection === 8 && targetSec === 9) {
        setDemoToInterfaceProgress(easedProgress);
      }
      else if (startSection === 9 && targetSec === 8) {
        setDemoToInterfaceProgress(1 - easedProgress);
      }
      else if (startSection === 9 && targetSec === 2) {
        // Going from INTERFACE back to DEMO Frame 3a - animate backwards and reset all chat states
        setDemoToInterfaceProgress(1 - easedProgress);
        // Reset all chat progress states to return to clean Frame 3a
        setSection3Progress(0);
        setSection4Progress(0);
        setSection5Progress(0);
        setSection6Progress(0);
        setSection7Progress(0);
        setSection8Progress(0);
        setSection9Progress(0);
        setSection11Progress(0);
      }
      // Section 11 (UI showcase→APPROACH): Blue swipe from UI showcase to APPROACH
      else if (startSection === 9 && targetSec === 10) {
        setSection11Progress(easedProgress);
      }
      else if (startSection === 10 && targetSec === 9) {
        setSection11Progress(1 - easedProgress);
      }
      // Section 12 (4a→4b): APPROACH accordion slide
      else if (startSection === 10 && targetSec === 11) {
        setSection12Progress(easedProgress);
      }
      else if (startSection === 11 && targetSec === 10) {
        setSection12Progress(1 - easedProgress);
      }
      // Section 13 (4b→4c): APPROACH accordion slide
      else if (startSection === 11 && targetSec === 12) {
        setSection13Progress(easedProgress);
      }
      else if (startSection === 12 && targetSec === 11) {
        setSection13Progress(1 - easedProgress);
      }
      // Skip transition: 4a→4c (section 10→12)
      else if (startSection === 10 && targetSec === 12) {
        // Animate both section12 and section13 progress simultaneously
        setSection12Progress(easedProgress);
        setSection13Progress(easedProgress);
      }
      // Skip transition: 4c→4a (section 12→10)
      else if (startSection === 12 && targetSec === 10) {
        // Animate both backwards simultaneously
        setSection12Progress(1 - easedProgress);
        setSection13Progress(1 - easedProgress);
      }
      // Section 14 (4c→5): APPROACH to WHY transition
      else if (startSection === 12 && targetSec === 13) {
        setSection14Progress(easedProgress);
      }
      else if (startSection === 13 && targetSec === 12) {
        setSection14Progress(1 - easedProgress);
      }
      // Section 15 (5→Footer): WHY to Footer reveal
      else if (startSection === 13 && targetSec === 14) {
        setSection15Progress(easedProgress);
      }
      // Section 15 reverse (Footer→WHY): Footer to WHY transition
      else if (startSection === 14 && targetSec === 13) {
        setSection15Progress(1 - easedProgress);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCurrentSection(targetSec);
        setIsAnimating(false);
        setIsAccordionAnimating(false); // Clear accordion lock when transition completes

        // Clear preset and re-enable accordion transitions
        setPresetAccordionSection(null);
        setTimeout(() => setSkipAccordionTransition(false), 50);
      }
    };

    requestAnimationFrame(animate);
  };

  // Scroll, touch, and keyboard navigation
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (isAnimating || isAccordionAnimating) {
        e.preventDefault();
        return;
      }

      if (e.deltaY > 0 && currentSection < 14) {
        e.preventDefault();
        // Special case: From Frame 3h (section 8) scroll to UI showcase (section 9)
        if (currentSection === 8) {
          // Animate to INTERFACE using same logic as button click
          setIsAnimating(true);
          setDisplaySection(9);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setDemoToInterfaceProgress(easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(9);
              setDisplaySection(9);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(1);
              setDemoToInterfaceProgress(0);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
            }
          };

          requestAnimationFrame(animate);
        }
        // Special case: From INTERFACE (section 9) scroll to APPROACH (section 10)
        else if (currentSection === 9) {
          // Animate to APPROACH using same logic as button click
          setPresetAccordionSection(10);
          setSkipAccordionTransition(true);
          setIsAnimating(true);
          setDisplaySection(10);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setSection11Progress(easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(10);
              setDisplaySection(10);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(1);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
              setPresetAccordionSection(null);
              setTimeout(() => setSkipAccordionTransition(false), 50);
            }
          };

          requestAnimationFrame(animate);
        }
        else {
          animateToSection(currentSection + 1);
        }
      } else if (e.deltaY < 0 && currentSection > 0) {
        e.preventDefault();
        // Special case: From APPROACH (section 10) scroll back to INTERFACE (section 9)
        if (currentSection === 10) {
          // Animate back to INTERFACE (reverse of APPROACH button)
          setIsAnimating(true);
          setDisplaySection(9);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setSection11Progress(1 - easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(9);
              setDisplaySection(9);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(0);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
            }
          };

          requestAnimationFrame(animate);
        }
        // Special case: From INTERFACE (section 9) scroll back to DEMO (section 2)
        else if (currentSection === 9) {
          animateToSection(2);
        }
        // Special case: From Frame 3b-3h (sections 3-8) scroll back to Frame 3a (section 2)
        else if (currentSection >= 3 && currentSection <= 8) {
          animateToSection(2);
        }
        else {
          animateToSection(currentSection - 1);
        }
      }
    };

    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (isAnimating || isAccordionAnimating) return;

      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchStartY - touchEndY;

      if (deltaY > 50 && currentSection < 14) {
        e.preventDefault();
        // Special case: From Frame 3h (section 8) scroll to UI showcase (section 9)
        if (currentSection === 8) {
          // Animate to INTERFACE using same logic as button click
          setIsAnimating(true);
          setDisplaySection(9);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setDemoToInterfaceProgress(easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(9);
              setDisplaySection(9);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(1);
              setDemoToInterfaceProgress(0);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
            }
          };

          requestAnimationFrame(animate);
        }
        // Special case: From INTERFACE (section 9) scroll to APPROACH (section 10)
        else if (currentSection === 9) {
          // Animate to APPROACH using same logic as button click
          setPresetAccordionSection(10);
          setSkipAccordionTransition(true);
          setIsAnimating(true);
          setDisplaySection(10);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setSection11Progress(easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(10);
              setDisplaySection(10);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(1);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
              setPresetAccordionSection(null);
              setTimeout(() => setSkipAccordionTransition(false), 50);
            }
          };

          requestAnimationFrame(animate);
        }
        else {
          animateToSection(currentSection + 1);
        }
      } else if (deltaY < -50 && currentSection > 0) {
        e.preventDefault();
        // Special case: From APPROACH (section 10) scroll back to INTERFACE (section 9)
        if (currentSection === 10) {
          // Animate back to INTERFACE (reverse of APPROACH button)
          setIsAnimating(true);
          setDisplaySection(9);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setSection11Progress(1 - easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(9);
              setDisplaySection(9);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(0);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
            }
          };

          requestAnimationFrame(animate);
        }
        // Special case: From INTERFACE (section 9) scroll back to DEMO (section 2)
        else if (currentSection === 9) {
          animateToSection(2);
        }
        // Special case: From Frame 3b-3h (sections 3-8) scroll back to Frame 3a (section 2)
        else if (currentSection >= 3 && currentSection <= 8) {
          animateToSection(2);
        }
        else {
          animateToSection(currentSection - 1);
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' && currentSection < 14) {
        // Special case: From Frame 3h (section 8) scroll to UI showcase (section 9)
        if (currentSection === 8) {
          // Animate to INTERFACE using same logic as button click
          setIsAnimating(true);
          setDisplaySection(9);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setDemoToInterfaceProgress(easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(9);
              setDisplaySection(9);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(1);
              setDemoToInterfaceProgress(0);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
            }
          };

          requestAnimationFrame(animate);
        }
        // Special case: From INTERFACE (section 9) scroll to APPROACH (section 10)
        else if (currentSection === 9) {
          // Animate to APPROACH using same logic as button click
          setPresetAccordionSection(10);
          setSkipAccordionTransition(true);
          setIsAnimating(true);
          setDisplaySection(10);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setSection11Progress(easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(10);
              setDisplaySection(10);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(1);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
              setPresetAccordionSection(null);
              setTimeout(() => setSkipAccordionTransition(false), 50);
            }
          };

          requestAnimationFrame(animate);
        }
        else {
          animateToSection(currentSection + 1);
        }
      } else if (e.key === 'ArrowUp' && currentSection > 0) {
        // Special case: From APPROACH (section 10) scroll back to INTERFACE (section 9)
        if (currentSection === 10) {
          // Animate back to INTERFACE (reverse of APPROACH button)
          setIsAnimating(true);
          setDisplaySection(9);
          const startTime = Date.now();
          const duration = ANIMATION_DURATIONS.default;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            setSection11Progress(1 - easedProgress);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCurrentSection(9);
              setDisplaySection(9);
              setIsAnimating(false);
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);
              setSection3Progress(1);
              setSection4Progress(1);
              setSection5Progress(1);
              setSection6Progress(1);
              setSection7Progress(1);
              setSection8Progress(1);
              setSection9Progress(0);
              setSection10Progress(0);
              setSection11Progress(0);
              setSection12Progress(0);
              setSection13Progress(0);
              setSection14Progress(0);
              setSection15Progress(0);
            }
          };

          requestAnimationFrame(animate);
        }
        // Special case: From INTERFACE (section 9) scroll back to DEMO (section 2)
        else if (currentSection === 9) {
          animateToSection(2);
        }
        // Special case: From Frame 3b-3h (sections 3-8) scroll back to Frame 3a (section 2)
        else if (currentSection >= 3 && currentSection <= 8) {
          animateToSection(2);
        }
        else {
          animateToSection(currentSection - 1);
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSection, isAnimating, isAccordionAnimating]);

  // Handle window resize for responsive footer
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track cursor position for Frame 1 phone rotation
  useEffect(() => {
    // Disable cursor rotation on mobile devices or during animations
    if (!CURSOR_ROTATION.enabled || currentSection > 1 || isMobile || isAnimating) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Only track cursor on Frame 1 (sections 0-1)
      if (currentSection > 1 || isAnimating) return;

      // Normalize cursor position to 0-1 range (0.5 = center)
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;

      setCursorX(x);
      setCursorY(y);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [currentSection, isMobile, isAnimating]);

  // Listen for reset event from Navbar
  useEffect(() => {
    const handleResetToFrame1 = () => {
      // Reset to frame 1 immediately
      setCurrentSection(0);
      setDisplaySection(0);
      setIsAnimating(false);

      // Reset all progress states
      setFrame1TextProgress(0);
      setFrame2TextProgress(0);
      setRockProgress(0);
      setFrame3Progress(0);
      setTypewriterProgress(0);
      resetAllTransitionProgress();
      setScrollIndicatorOpacity(1);
    };

    window.addEventListener('resetToFrame1', handleResetToFrame1 as EventListener);
    return () => window.removeEventListener('resetToFrame1', handleResetToFrame1 as EventListener);
  }, []);


  // Force scroll to top on mount (prevent browser scroll restoration)
  useEffect(() => {
    // Aggressively reset scroll position (critical for mobile)
    const resetScroll = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Mobile Safari additional fix - scroll to top using scrollTo
      if (window.visualViewport) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant' as ScrollBehavior
        });
      }
    };

    resetScroll();

    // Run again after a brief delay to catch late scroll restoration attempts
    setTimeout(resetScroll, 100);
    setTimeout(resetScroll, 250);
  }, []);

  // Loading screen - hide after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Track mouse position for tooltip (section 9 UI showcase)
  useEffect(() => {
    if (currentSection !== 9 || isMobile) return;

    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [currentSection, isMobile]);

  // Typewriter effect for greeting text - tied directly to frame3Progress
  useEffect(() => {
    setGreetingTypewriter(typewriterProgress);
  }, [typewriterProgress]);

  // Cycle through adjectives on Frame 1
  useEffect(() => {
    // Only cycle when on Frame 1 (section 0 or 1)
    if (currentSection > 1) return;

    const interval = setInterval(() => {
      // Start transition - save current as prev
      setPrevAdjectiveIndex(currentAdjectiveIndex);
      setIsTransitioning(true);

      // Update to next adjective
      setCurrentAdjectiveIndex((prevIndex) => (prevIndex + 1) % adjectives.length);

      // Clear transition state after animation completes
      setTimeout(() => {
        setIsTransitioning(false);
        setPrevAdjectiveIndex(null);
      }, 500); // Match animation duration
    }, 2500); // Change word every 2.5 seconds (includes transition time)

    return () => clearInterval(interval);
  }, [currentSection, adjectives.length, currentAdjectiveIndex]);

  // Auto-rotation for mobile on Frame 1
  useEffect(() => {
    // Only auto-rotate on mobile when on Frame 1 (section 0 or 1)
    if (!isMobile || currentSection > 1) return;

    const interval = setInterval(() => {
      setAutoRotationAngle((prevAngle) => {
        const newAngle = prevAngle + 0.003; // Slower rotation for better viewing
        return newAngle >= 1 ? 0 : newAngle; // Loop back to 0 after full rotation
      });
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [isMobile, currentSection]);

  // Button click handler
  const handleDemoClick = () => {
    if (!chatEnabled) {
      setChatEnabled(true);
    }
    animateToSection(3); // Go to Frame 3b (logo at bottom + "scroll for conversation")
  };

  // Shared text rendering styles for consistent font weight appearance
  const textRenderingStyle = {
    WebkitFontSmoothing: 'antialiased' as const,
    MozOsxFontSmoothing: 'grayscale' as const,
    textRendering: 'geometricPrecision' as const,
  };

  // Frame 1 text (slides out to the left)
  const frame1TextStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%) translateX(-${frame1TextProgress * 150}vw)`,
    opacity: 1 - frame1TextProgress,
    zIndex: 10,
    textAlign: 'center' as const,
    width: '90%',
    maxWidth: '1200px',
    pointerEvents: 'auto' as const,
    ...textRenderingStyle
  };

  // Frame 2 text (slides in from right in Frame 2, slides out left in Frame 3)
  const frame2TextStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%) translateX(${(1 - frame2TextProgress) * 150}vw) translateX(-${frame3Progress * 150}vw)`,
    opacity: frame2TextProgress * (1 - frame3Progress),
    zIndex: 10,
    textAlign: 'center' as const,
    width: '90%',
    maxWidth: '1200px',
    pointerEvents: 'auto' as const,
    ...textRenderingStyle
  };

  return (
    <>
      {/* Loading screen */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#000',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ width: '7.5%', maxWidth: '45px' }}>
            <Lottie
              animationData={stonesAnimation}
              loop={true}
              autoplay={true}
            />
          </div>
        </div>
      )}

      <div
        className="relative bg-black"
        style={{
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          margin: 0,
          padding: 0,
          backgroundColor: '#000'
        }}
      >
      {/* Frame 1: "take back control" with tilted 3D rock */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1
      }}>
        <div style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          visibility: (hidePhone || (!isAnimating && displaySection <= 1 && frame3Progress > 0.1)) ? 'hidden' : 'visible'
        }}>
          {displaySection <= 2 && !(!isAnimating && currentSection === 2 && frame3Progress > 0.99) && section10Progress === 0 && section11Progress === 0 && (
            <StonePhone3D
              key="stone-phone-3d"
              rotationProgress={rockProgress}
              scaleMultiplier={1 + (frame3Progress * 4)}
              opacity={1 - frame3Progress}
              cursorX={cursorX}
              cursorY={cursorY}
              cursorRotationSettings={memoizedCursorSettings}
              isMobile={isMobile}
              autoRotationAngle={autoRotationAngle}
              demoProgress={frame3Progress}
            />
          )}
        </div>
        <div style={frame1TextStyle}>
          <h1 className="header_1" style={{ color: '#ffffff', marginBottom: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' }}>
            <span style={{ fontWeight: 'var(--weight-medium)' }}>something</span>
            {/* Mask container for sliding text animation */}
            <span className="adjective-mask" style={{
              display: 'block',
              overflow: 'hidden',
              height: '1.4em',
              position: 'relative',
              width: '100%',
              textAlign: 'center'
            }}>
              {/* Exiting text (slides up and out) */}
              {isTransitioning && prevAdjectiveIndex !== null && (
                <span
                  className="adjective-exit"
                  style={{
                    color: '#799fb5',
                    fontWeight: 'var(--weight-medium)',
                    display: 'block',
                    position: 'absolute',
                    width: '100%',
                    left: 0
                  }}
                >
                  {adjectives[prevAdjectiveIndex]}
                </span>
              )}
              {/* Entering text (slides up from below) */}
              <span
                key={currentAdjectiveIndex}
                className={isTransitioning ? 'adjective-enter' : ''}
                style={{
                  color: '#799fb5',
                  fontWeight: 'var(--weight-medium)',
                  display: 'block'
                }}
              >
                {adjectives[currentAdjectiveIndex]}
              </span>
            </span>
          </h1>
        </div>
      </div>

      {/* Frame 2: "meet the stone" with straightened 3D rock */}
      {frame2TextProgress > 0 && (
        <div style={frame2TextStyle}>
          <h1 className="header_1" style={{ color: '#ffffff', marginBottom: '0.5rem' }}>
            meet the stone
          </h1>
          <p className="paragraph_1" style={{ color: '#799fb5', margin: 0 }}>
            an AI powered phone that gives more than it takes
          </p>
        </div>
      )}

      {/* Frame 3a: Logo + AI greeting + button */}
      {frame3Progress > 0 && (currentSection <= 8 || section10Progress > 0 || section11Progress > 0) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 20,
          opacity: frame3Progress,
          pointerEvents: frame3Progress > 0.5 ? 'auto' : 'none'
        }}>
          {/* Black header cover for frames 3b-3h - masks chat messages below header */}
          {currentSection >= 3 && currentSection <= 8 && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '80px',
              backgroundColor: '#000',
              zIndex: 26
            }} />
          )}

          {/* AI greeting text */}
          {typewriterProgress > 0 && (() => {
            const greetingLine1 = "hi,";
            const greetingLine2 = "how can I help you today?";
            const totalChars = greetingLine1.length + greetingLine2.length;
            const visibleChars = Math.floor(totalChars * greetingTypewriter);

            let line1Text = "";
            let line2Text = "";
            let showCursor = greetingTypewriter > 0 && greetingTypewriter < 1;
            let cursorOnLine2 = false;

            if (visibleChars <= greetingLine1.length) {
              line1Text = greetingLine1.slice(0, visibleChars);
            } else {
              line1Text = greetingLine1;
              line2Text = greetingLine2.slice(0, visibleChars - greetingLine1.length);
              cursorOnLine2 = true;
            }

            return (
              <div style={{
                position: 'absolute',
                top: section3Progress > 0
                  ? '50%'
                  : `${50 + (section3Progress * 35)}%`,
                left: '50%',
                transform: isMobile
                  ? 'translate(-50%, calc(-50% - 180px))'
                  : 'translate(-50%, calc(-50% - 220px))',
                textAlign: 'center',
                zIndex: 21,
                opacity: 1 - section3Progress,
                maxWidth: '90%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                willChange: 'opacity'
              }}>
                <p className="paragraph_1" style={{
                  color: '#ffffff',
                  margin: 0,
                  whiteSpace: 'nowrap'
                }}>
                  <span style={{ display: 'block' }}>
                    {line1Text}{showCursor && !cursorOnLine2 && '|'}
                  </span>
                  <span style={{ display: 'block' }}>
                    {line2Text}{showCursor && cursorOnLine2 && '|'}
                  </span>
                </p>
              </div>
            );
          })()}

          {/* Logo mark - PERFECTLY CENTERED, scales from 0 on 2>3a, shrinks and moves down on 3a>3b */}
          {/* Hide logo when Stone is typing and show animation instead */}
          {!(section5Progress > 0 && section5Progress < 1) &&
           !(section7Progress > 0 && section7Progress < 1) &&
           !(section9Progress > 0 && section9Progress < 1) && (
            <div style={{
              position: 'absolute',
              top: `${50 + (section3Progress * 35)}%`,
              left: '50%',
              transform: `translate(-50%, -50%) scale(${frame3Progress * (1 - (section3Progress * 0.7))}) translateZ(0)`,
              zIndex: 30,
              willChange: 'transform',
              contain: 'layout style paint'
            }}>
              <img
                src="/assets/icons/stone_logo_mark.svg"
                alt="Stone Logo"
                style={{ width: '160px', height: 'auto', display: 'block' }}
              />
            </div>
          )}

          {/* Stones animation - plays when Stone is typing */}
          {((section5Progress > 0 && section5Progress < 1) ||
            (section7Progress > 0 && section7Progress < 1) ||
            (section9Progress > 0 && section9Progress < 1)) && (
            <div style={{
              position: 'absolute',
              top: `${50 + (section3Progress * 35)}%`,
              left: '50%',
              transform: `translate(-50%, -50%) scale(${frame3Progress * (1 - (section3Progress * 0.7))}) translateZ(0)`,
              zIndex: 30,
              width: '160px',
              willChange: 'transform',
              contain: 'layout style paint'
            }}>
              <Lottie
                animationData={stonesAnimation}
                loop={true}
                autoplay={true}
                rendererSettings={{
                  preserveAspectRatio: 'xMidYMid slice',
                  progressiveLoad: true
                }}
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
          )}

          {/* Chat messages */}
          {section3Progress > 0 && (
            <div className="chat-container" style={{
              position: 'fixed',
              bottom: '23vh',
              left: '50%',
              transform: 'translateX(-50%) translateZ(0)',
              width: '90%',
              maxWidth: 'var(--content-max-width)',
              zIndex: 25,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              willChange: 'contents'
            }}>
              {/* User message 1 */}
              {section4Progress > 0 && (
                <div style={{
                  backgroundColor: '#000000',
                  padding: '16px',
                  borderRadius: '12px',
                  alignSelf: 'flex-end',
                  maxWidth: '80%'
                }}>
                  <p className="paragraph_2" style={{ color: '#799fb5', margin: 0, fontFamily: 'var(--font-body)' }}>
                    {user1Visible}
                    {user1Cursor && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>
                </div>
              )}

              {/* Stone message 1 - Parts 1 & 2: Plain text */}
              {section5Progress > 0 && (stone1Part1Visible.length > 0 || stone1Part2Visible.length > 0) && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%'
                }}>
                  <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, fontFamily: 'var(--font-body)', whiteSpace: 'pre-wrap' }}>
                    {stone1Part1Visible}
                    {stone1CursorOnPart1 && <span style={{ opacity: 0.5 }}>|</span>}
                    {stone1Part1Visible.length === stoneText1Part1.length && stone1Part2Visible.length > 0 && (
                      <>
                        {'\n'}{stone1Part2Visible}
                        {stone1CursorOnPart2 && <span style={{ opacity: 0.5 }}>|</span>}
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Stone message 1 - Part 3: Tool call box with suggested reply */}
              {section5Progress > 0 && stone1Part3Visible.length > 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%',
                  backgroundColor: section6Progress > 0 ? '#000000' : '#1a1a1a',
                  border: section6Progress > 0 ? 'none' : '1px solid #333',
                  borderRadius: '8px',
                  padding: '12px',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}>
                  {/* Tool label or Sent status */}
                  <div style={{
                    fontSize: '10px',
                    color: section6Progress > 0 ? '#799fb5' : '#888',
                    fontFamily: 'var(--font-body)',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    {section6Progress > 0 ? (
                      <>
                        <span>Sent</span>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 6L5 10L11 2" stroke="#799fb5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </>
                    ) : (
                      'Send Text Message'
                    )}
                  </div>

                  {/* Message content */}
                  <p className="paragraph_2" style={{
                    color: section6Progress > 0 ? '#799fb5' : '#ffffff',
                    margin: 0,
                    fontFamily: 'var(--font-body)',
                    fontStyle: 'italic',
                    fontSize: '14px',
                    transition: 'color 0.3s ease'
                  }}>
                    {stone1Part3Visible}
                    {stone1CursorOnPart3 && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>

                  {/* Action buttons - only show before user responds */}
                  {section6Progress === 0 && !stone1CursorOnPart3 && stone1Part3Visible.length === stoneText1Part3.length && (
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      justifyContent: 'flex-end',
                      marginTop: '8px',
                      pointerEvents: 'auto'
                    }}>
                      <span style={{
                        padding: '3px 8px',
                        color: '#888',
                        fontFamily: 'var(--font-body)',
                        fontSize: '11px'
                      }}>
                        no
                      </span>
                      <span style={{
                        padding: '3px 8px',
                        color: '#888',
                        fontFamily: 'var(--font-body)',
                        fontSize: '11px'
                      }}>
                        edit
                      </span>
                      <button
                        onClick={() => {
                          // Advance to next frame in sequence (scroll forward)
                          if (!isAnimating) {
                            if (currentSection === 2) {
                              animateToSection(3);
                            } else if (currentSection >= 3 && currentSection < 8) {
                              animateToSection(currentSection + 1);
                            }
                          }
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          padding: '3px 8px',
                          color: '#799fb5',
                          fontFamily: 'var(--font-body)',
                          fontSize: '11px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        yes
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* User message 2 */}
              {section6Progress > 0 && (
                <div style={{
                  backgroundColor: '#000000',
                  padding: '16px',
                  borderRadius: '12px',
                  alignSelf: 'flex-end',
                  maxWidth: '80%'
                }}>
                  <p className="paragraph_2" style={{ color: '#799fb5', margin: 0, fontFamily: 'var(--font-body)' }}>
                    {user2Visible}
                    {user2Cursor && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>
                </div>
              )}

              {/* Stone message 2 - Part 1: Anniversary notice */}
              {section7Progress > 0 && stone2Part1Visible.length > 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%'
                }}>
                  <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, fontFamily: 'var(--font-body)' }}>
                    {stone2Part1Visible}
                    {stone2CursorOnPart1 && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>
                </div>
              )}

              {/* Stone message 2 - Part 2: Bookmarked restaurant notice */}
              {section7Progress > 0 && stone2Part2Visible.length > 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%'
                }}>
                  <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, fontFamily: 'var(--font-body)' }}>
                    {stone2Part2Visible}
                    {stone2CursorOnPart2 && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>
                </div>
              )}

              {/* Tool call: Search for Availability - In progress (shown briefly) */}
              {section7Progress > 0 && stone2Part2Visible.length === stoneText2Part2.length && !stone2CursorOnPart2 && stone2Part3Visible.length === 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '8px'
                }}>
                  <div style={{
                    fontSize: '10px',
                    color: '#888',
                    fontFamily: 'var(--font-body)',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Searching Availability
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#aaa',
                    fontFamily: 'var(--font-body)',
                    marginBottom: '8px'
                  }}>
                    Accessing fiorellasf.com...
                  </div>
                  <div style={{
                    height: '4px',
                    backgroundColor: '#333',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      backgroundColor: '#799fb5',
                      width: '70%',
                      animation: 'pulse 1s ease-in-out infinite'
                    }} />
                  </div>
                </div>
              )}

              {/* Tool call result: Availability search complete - stays visible */}
              {section7Progress > 0 && stone2Part2Visible.length === stoneText2Part2.length && stone2Part3Visible.length > 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #799fb5',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '8px'
                }}>
                  <div style={{
                    fontSize: '10px',
                    color: '#799fb5',
                    fontFamily: 'var(--font-body)',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>Search Complete</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 6L5 10L11 2" stroke="#799fb5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#ffffff',
                    fontFamily: 'var(--font-body)'
                  }}>
                    Friday 6:30 PM – Table for 2 available
                  </div>
                </div>
              )}

              {/* Stone message 2 - Part 3: Availability confirmation */}
              {section7Progress > 0 && stone2Part3Visible.length > 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%'
                }}>
                  <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, fontFamily: 'var(--font-body)' }}>
                    {stone2Part3Visible}
                    {stone2CursorOnPart3 && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>
                </div>
              )}

              {/* Stone message 2 - Part 4: Tool call box for reservation request - Initial state */}
              {section7Progress > 0 && stone2Part4Visible.length > 0 && section8Progress === 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  padding: '12px',
                  position: 'relative'
                }}>
                  {/* Tool label */}
                  <div style={{
                    fontSize: '10px',
                    color: '#888',
                    fontFamily: 'var(--font-body)',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Book Restaurant
                  </div>

                  {/* Message content */}
                  <p className="paragraph_2" style={{
                    color: '#ffffff',
                    margin: 0,
                    fontFamily: 'var(--font-body)',
                    fontSize: '14px'
                  }}>
                    {stone2Part4Visible}
                    {stone2CursorOnPart4 && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>

                  {/* Action buttons - only show when text is complete */}
                  {!stone2CursorOnPart4 && stone2Part4Visible.length === stoneText2Part4.length && (
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      justifyContent: 'flex-end',
                      marginTop: '8px',
                      pointerEvents: 'auto'
                    }}>
                      <span style={{
                        padding: '3px 8px',
                        color: '#888',
                        fontFamily: 'var(--font-body)',
                        fontSize: '11px'
                      }}>
                        no
                      </span>
                      <span style={{
                        padding: '3px 8px',
                        color: '#888',
                        fontFamily: 'var(--font-body)',
                        fontSize: '11px'
                      }}>
                        edit
                      </span>
                      <button
                        onClick={() => {
                          // Advance to next frame in sequence (scroll forward)
                          if (!isAnimating) {
                            if (currentSection >= 3 && currentSection < 8) {
                              animateToSection(currentSection + 1);
                            }
                          }
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          padding: '3px 8px',
                          color: '#799fb5',
                          fontFamily: 'var(--font-body)',
                          fontSize: '11px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        yes
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Stone message 2 - Part 4: Tool call box - Confirmed state (after user says yes) */}
              {section7Progress > 0 && stone2Part4Visible.length > 0 && section8Progress > 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%',
                  backgroundColor: '#000000',
                  borderRadius: '8px',
                  padding: '12px',
                  position: 'relative'
                }}>
                  {/* Booked status */}
                  <div style={{
                    fontSize: '10px',
                    color: '#799fb5',
                    fontFamily: 'var(--font-body)',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>Booked</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 6L5 10L11 2" stroke="#799fb5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>

                  {/* Message content */}
                  <p className="paragraph_2" style={{
                    color: '#799fb5',
                    margin: 0,
                    fontFamily: 'var(--font-body)',
                    fontSize: '14px'
                  }}>
                    {stone2Part4Visible}
                  </p>
                </div>
              )}

              {/* User message 3 */}
              {section8Progress > 0 && (
                <div style={{
                  backgroundColor: '#000000',
                  padding: '16px',
                  borderRadius: '12px',
                  alignSelf: 'flex-end',
                  maxWidth: '80%'
                }}>
                  <p className="paragraph_2" style={{ color: '#799fb5', margin: 0, fontFamily: 'var(--font-body)' }}>
                    {user3Visible}
                    {user3Cursor && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>
                </div>
              )}


              {/* Stone message 3 - Part 1: Main response */}
              {section9Progress > 0 && stone3Part1Visible.length > 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%'
                }}>
                  <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, fontFamily: 'var(--font-body)' }}>
                    {stone3Part1Visible}
                    {stone3CursorOnPart1 && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>
                </div>
              )}

              {/* Stone message 3 - Part 2: Closing text */}
              {section9Progress > 0 && stone3Part2Visible.length > 0 && (
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '100%'
                }}>
                  <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, fontFamily: 'var(--font-body)' }}>
                    {stone3Part2Visible}
                    {stone3CursorOnPart2 && <span style={{ opacity: 0.5 }}>|</span>}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* INTERFACE button for UI showcase (section 9) */}
      {displaySection === 9 && (
        <div
          style={{ ...navButtonBaseStyle, right: LAYOUT.spacing.small, zIndex: LAYOUT.zIndex.navigation }}
          onClick={() => {
            // Jump to APPROACH (section 10)
            setCurrentSection(10);
            setDisplaySection(10);
            setIsAnimating(false);

            // Set all previous frame progress states to complete
            setFrame1TextProgress(1);
            setFrame2TextProgress(1);
            setRockProgress(1);
            setFrame3Progress(1);
            setTypewriterProgress(1);

            // Reset all transition progress states
            resetAllTransitionProgress();
          }}
        >
          <span>APPROACH</span>
          <ArrowSVG />
        </div>
      )}

      {/* WHY button for APPROACH frames (4a-4c) - White color on blue background */}
      {displaySection >= 10 && displaySection <= 12 && (
        <div
          style={{ ...navButtonBaseStyle, right: LAYOUT.spacing.small, zIndex: LAYOUT.zIndex.navigation, color: COLORS.white }}
          onClick={() => {
            // Instant jump to Frame 5 (section 13)
            setCurrentSection(13);
            setDisplaySection(13);
            setIsAnimating(false);

            // Set all previous frame progress states to complete
            setFrame1TextProgress(1);
            setFrame2TextProgress(1);
            setRockProgress(1);
            setFrame3Progress(1);
            setTypewriterProgress(1);

            // Reset all transition progress states
            resetAllTransitionProgress();
          }}
        >
          <span>WHY</span>
          <ArrowSVG />
        </div>
      )}

      {/* ORDER button for WHY frame (section 13) */}
      {displaySection === 13 && (
        <div
          style={{ ...navButtonBaseStyle, right: LAYOUT.spacing.small, zIndex: LAYOUT.zIndex.navigation }}
          {...createHoverHandlers()}
          onClick={() => {
            window.location.href = '/order';
          }}
        >
          <span>ORDER</span>
          <ArrowSVG rotation={-90} />
        </div>
      )}

      {/* UI Showcase Frame (Section 9): Show Stone's app interface */}
      {((demoToInterfaceProgress > 0 && demoToInterfaceProgress < 1) || currentSection === 9 || (section11Progress > 0 && section11Progress < 1)) && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#000000',
          zIndex: 25,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transform: demoToInterfaceProgress > 0 && demoToInterfaceProgress < 1
            ? `translateY(${(1 - demoToInterfaceProgress) * 100}vh)`
            : section11Progress > 0 && section11Progress < 1
            ? `translateY(${-section11Progress * 100}vh)`
            : section11Progress >= 1
            ? `translateY(-100vh)`
            : 'translateY(0)'
        }}>
          {/* Title */}
          <h2 className="header_2" style={{
            color: '#ffffff',
            marginBottom: isMobile ? '6vh' : '8vh',
            marginTop: isMobile ? '12vh' : '4vh',
            textAlign: 'center',
            position: 'relative',
            zIndex: 20
          }}>
            simple home interface
          </h2>

          {/* Apps grid - centered on page */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            columnGap: isMobile ? '1rem' : '1.5rem',
            rowGap: isMobile ? '2rem' : '2.5rem',
            placeItems: 'center'
          }}>
              {apps.map((app) => (
                <button
                  key={app.name}
                  type="button"
                  style={{
                    height: isMobile ? '60px' : '70px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Georgia, serif',
                    fontSize: isMobile ? '18px' : '20px',
                    color: hoveredApp === app.name ? '#799fb5' : '#ffffff',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.3s ease, text-shadow 0.3s ease',
                    position: 'relative',
                    pointerEvents: 'auto',
                    // Remove default mobile tap highlight
                    WebkitTapHighlightColor: 'transparent',
                    outline: 'none',
                    // Mobile: subtle text glow when tapped, desktop: no glow
                    textShadow: isMobile && hoveredApp === app.name
                      ? '0 0 8px rgba(255, 255, 255, 0.3)'
                      : 'none'
                  }}
                  onMouseEnter={() => !isMobile && setHoveredApp(app.name)}
                  onMouseLeave={() => !isMobile && setHoveredApp(null)}
                  onClick={() => {
                    if (isMobile) {
                      // On mobile: toggle the description with a single tap
                      setHoveredApp(hoveredApp === app.name ? null : app.name);
                    }
                  }}
                >
                  {app.name}
                </button>
              ))}
            </div>

          {/* Desktop: Description follows cursor */}
          {!isMobile && hoveredApp && (
            <div style={{
              position: 'fixed',
              left: `${cursorPosition.x + 20}px`,
              top: `${cursorPosition.y + 20}px`,
              pointerEvents: 'none',
              zIndex: 1000,
              padding: '0.75rem 1rem',
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              maxWidth: '280px',
              animation: 'fadeIn 0.2s ease'
            }}>
              <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, fontSize: '14px' }}>
                {apps.find(a => a.name === hoveredApp)?.description}
              </p>
            </div>
          )}

          {/* Mobile: Description overlay on top (over title) */}
          {isMobile && hoveredApp && (
            <div style={{
              position: 'absolute',
              top: isMobile ? '12vh' : '4vh',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '90%',
              maxWidth: '600px',
              padding: '1.5rem',
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              textAlign: 'center',
              animation: 'fadeInUp 0.3s ease',
              zIndex: 30,
              pointerEvents: 'auto'
            }}
            onClick={() => setHoveredApp(null)}
            >
              <p className="paragraph_2" style={{ color: '#ffffff', margin: 0 }}>
                {apps.find(a => a.name === hoveredApp)?.description}
              </p>
              <p style={{ color: '#666', fontSize: '12px', marginTop: '0.5rem', marginBottom: 0 }}>
                Tap to close
              </p>
            </div>
          )}
        </div>
      )}

      {/* Frame 4a/4b/4c: Blue background with content */}
      {((section10Progress > 0 || section11Progress > 0 || (section14Progress > 0 && section14Progress < 1)) || (currentSection >= 10 && currentSection <= 12)) && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#799fb5',
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transform: section10Progress > 0 && section10Progress < 1
            ? `translateY(${(1 - section10Progress) * 100}vh)`
            : section11Progress > 0 && section11Progress < 1
            ? `translateY(${(1 - section11Progress) * 100}vh)`
            : 'translateY(0)'
        }}>
          {/* Centered content wrapper */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-start' : 'center',
            width: '100%',
            height: '100%'
          }}>
            {/* "our approach" title */}
            <h1 className="header_1" style={{
              color: '#ffffff',
              margin: isMobile ? '12vh 0 3vh 0' : '0 0 4vh 0',
              width: '90%',
              maxWidth: 'var(--content-max-width)',
              textAlign: 'left'
            }}>
              our approach
            </h1>

            {/* Sliding accordion with fixed content height */}
            <div style={{
              width: '90%',
              maxWidth: 'var(--content-max-width)',
              position: 'relative'
            }}>
              {/* Fixed content height constant */}
              {(() => {
                const CONTENT_HEIGHT = isMobile ? 220 : 320; // Fixed height for all content areas
                const HEADER_HEIGHT = isMobile ? '55px' : '70px'; // Responsive header height

                return (
                  <>
                    {/* Section 1 - Minimalist interface */}
                    <div style={{
                      position: 'relative',
                      height: (currentSection === 10 || presetAccordionSection === 10) ? `calc(${HEADER_HEIGHT} + ${CONTENT_HEIGHT}px)` : HEADER_HEIGHT,
                      overflow: 'hidden',
                      transition: skipAccordionTransition ? 'none' : 'height 1000ms cubic-bezier(0.4, 0, 0.2, 1)',
                      marginBottom: '16px'
                    }}>
                      <h2 className="header_2 frame4-title" style={{
                        color: '#ffffff',
                        margin: '0 0 24px 0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (currentSection !== 10 && !isAnimating) {
                          animateToSection(10);
                        }
                      }}>
                        <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>1</span>
                        <span style={{ flex: 1 }}>a minimalist interface</span>
                      </h2>

                      {/* Content area - always present, revealed by parent height */}
                      <div style={{
                        height: `${CONTENT_HEIGHT}px`,
                        opacity: (currentSection === 10 || presetAccordionSection === 10) ? 1 : 0,
                        transition: skipAccordionTransition ? 'none' : 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        <div style={{ width: '30%', minWidth: '120px', maxWidth: '180px', height: '150px', marginBottom: '0.5vh' }}>
                          <Lottie
                            key={`more-life-${currentSection}`}
                            animationData={moreLifeAnimation}
                            loop={false}
                            autoplay={currentSection === 10 || presetAccordionSection === 10}
                            rendererSettings={{ preserveAspectRatio: 'xMinYMid meet' }}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </div>
                        <p className="paragraph_2" style={{ color: '#ffffff', margin: 0 }}>
                          Clean, focused, distraction-free. No infinite feeds, no attention traps, no algorithmic manipulation. Just what you need, when you need it—then it disappears.
                        </p>
                      </div>
                    </div>

                    {/* Section 2 - An assistant that actually works */}
                    <div style={{
                      position: 'relative',
                      height: currentSection === 11 ? `calc(${HEADER_HEIGHT} + ${CONTENT_HEIGHT}px)` : HEADER_HEIGHT,
                      overflow: 'hidden',
                      transition: skipAccordionTransition ? 'none' : 'height 1000ms cubic-bezier(0.4, 0, 0.2, 1)',
                      marginBottom: '16px'
                    }}>
                      <h2 className="header_2 frame4-title" style={{
                        color: '#ffffff',
                        margin: '0 0 24px 0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (currentSection !== 11 && !isAnimating) {
                          animateToSection(11);
                        }
                      }}>
                        <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>2</span>
                        <span style={{ flex: 1 }}>a functional assistant</span>
                      </h2>

                      {/* Content area */}
                      <div style={{
                        height: `${CONTENT_HEIGHT}px`,
                        opacity: currentSection === 11 ? 1 : 0,
                        transition: skipAccordionTransition ? 'none' : 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        <div style={{ width: '30%', minWidth: '120px', maxWidth: '180px', height: '150px', marginBottom: '0.5vh' }}>
                          <Lottie
                            key={`long-term-${currentSection}`}
                            animationData={longTermAnimation}
                            loop={false}
                            autoplay={currentSection === 11}
                            rendererSettings={{ preserveAspectRatio: 'xMinYMid meet' }}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </div>
                        <p className="paragraph_2" style={{ color: '#ffffff', margin: 0 }}>
                          Calls, texts, research—everything you need to do on your phone, actually done. No "I can't do that," no broken promises, no wasting your time. Real tasks, real results.
                        </p>
                      </div>
                    </div>

                    {/* Section 3 - AI that knows you and puts you first */}
                    <div style={{
                      position: 'relative',
                      height: (currentSection === 12 || presetAccordionSection === 12) ? `calc(${HEADER_HEIGHT} + ${CONTENT_HEIGHT}px)` : HEADER_HEIGHT,
                      overflow: 'hidden',
                      transition: skipAccordionTransition ? 'none' : 'height 1000ms cubic-bezier(0.4, 0, 0.2, 1)',
                      marginBottom: '16px'
                    }}>
                      <h2 className="header_2 frame4-title" style={{
                        color: '#ffffff',
                        margin: '0 0 24px 0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (currentSection !== 12 && !isAnimating) {
                          animateToSection(12);
                        }
                      }}>
                        <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>3</span>
                        <span style={{ flex: 1 }}>an AI that knows you</span>
                      </h2>

                      {/* Content area */}
                      <div style={{
                        height: `${CONTENT_HEIGHT}px`,
                        opacity: (currentSection === 12 || presetAccordionSection === 12) ? 1 : 0,
                        transition: skipAccordionTransition ? 'none' : 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        <div style={{ width: '30%', minWidth: '120px', maxWidth: '180px', height: '150px', marginBottom: '0.5vh' }}>
                          <Lottie
                            key={`friction-${currentSection}`}
                            animationData={functionOverFrictionAnimation}
                            loop={false}
                            autoplay={currentSection === 12 || presetAccordionSection === 12}
                            rendererSettings={{ preserveAspectRatio: 'xMinYMid meet' }}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </div>
                        <p className="paragraph_2" style={{ color: '#ffffff', margin: 0 }}>
                          Learns your patterns, priorities, and preferences—not to sell you ads, but to serve you better. Privacy-first intelligence that works for you, not against you.
                        </p>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Black header cover for mobile - masks content sliding below navbar (only during footer slide-up) */}
      {isMobile && section15Progress > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '80px',
          backgroundColor: '#000',
          zIndex: 999,
          pointerEvents: 'none'
        }} />
      )}

      {/* Frame 5 (WHY page): Black frame at bottom that slides up slightly to reveal footer */}
      {(section14Progress > 0 || currentSection === 13 || currentSection === 14 || section15Progress > 0) && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100vw',
          minHeight: '100vh',
          backgroundColor: '#000000',
          zIndex: 40,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: '10vh',
          transform: section15Progress > 0
            ? isMobile
              ? `translateY(-${section15Progress * 180}px)`
              : `translateY(-${section15Progress * LAYOUT.footer.heightVh}vh)`
            : section14Progress > 0 && section14Progress < 1
            ? `translateY(${(1 - section14Progress) * 100}vh)`
            : 'translateY(0)'
        }}>
          {/* Frame 5 content */}
          <div style={{
            width: '90%',
            maxWidth: 'var(--content-max-width)',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            gap: '4vh',
            marginTop: isMobile ? '14vh' : '0'
          }}>
            {/* Main headline */}
            <h2 className="header_2" style={{ color: '#ffffff', margin: 0, lineHeight: 1.2 }}>
              Somewhere along the line, our phones stopped being tools.
            </h2>

            {/* Body paragraphs */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
              <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, lineHeight: 1.5 }}>
                They hijack our attention and profit from our distraction.
              </p>
              <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, lineHeight: 1.5 }}>
                We believe there's a better way.
              </p>
              <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, lineHeight: 1.5 }}>
                Where technology serves humans, not advertisers.
              </p>
              <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, lineHeight: 1.5 }}>
                Where your phone protects your focus instead of fragmenting it.
              </p>
              <p className="paragraph_2" style={{ color: '#ffffff', margin: 0, lineHeight: 1.5 }}>
                Where being connected means more presence in the real world, not less.
              </p>
            </div>

            {/* Order button */}
            <button
              onClick={() => window.location.href = '/order'}
              className="button-primary button-large"
              style={{
                alignSelf: 'center',
                marginTop: '2vh'
              }}
            >
              order the stone
            </button>
          </div>
        </div>
      )}

      {/* Footer: Static at bottom, revealed when WHY page slides up */}
      {(section15Progress > 0 || currentSection === 14) && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100vw',
          height: isMobile ? '180px' : `${LAYOUT.footer.heightVh}vh`,
          backgroundColor: '#1e1e1e',
          zIndex: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '10px 20px 5px' : '20px 20px 10px',
          overflowX: 'auto'
        }}>
          <footer className="footer" style={{ 
            backgroundColor: 'transparent',
            borderTop: 'none',
            color: '#fff',
            padding: '0',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div className="footer-container" style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              justifyContent: 'center',
              alignItems: 'flex-start',
              width: '100%',
              maxWidth: isMobile ? '100%' : '1200px',
              margin: '0 auto',
              padding: '0 20px',
              gap: isMobile ? '30px' : '80px'
            }}>
              <div className="footer-section" style={{
                flex: 'none',
                display: 'block',
                marginBottom: '0'
              }}>
                <h3 className="footer-brand" style={{
                  fontSize: isMobile ? '16px' : '20px',
                  fontFamily: 'var(--font-primary)',
                  letterSpacing: '0',
                  marginBottom: '8px',
                  opacity: 0.9,
                  color: '#ffffff',
                  whiteSpace: 'nowrap'
                }}>pebble</h3>
                <div className="footer-address" style={{
                  fontSize: isMobile ? '12px' : '14px',
                  lineHeight: '1.6',
                  opacity: 0.7
                }}>
                  <p style={{ margin: '3px 0', whiteSpace: 'nowrap' }}>2323 3rd Street</p>
                  <p style={{ margin: '3px 0', whiteSpace: 'nowrap' }}>san francisco, ca 94107</p>
                </div>
              </div>

              <div className="footer-section" style={{
                flex: 'none',
                display: 'block',
                marginBottom: '0'
              }}>
                <h4 className="footer-title" style={{
                  fontSize: isMobile ? '14px' : '16px',
                  letterSpacing: '1px',
                  marginBottom: '8px',
                  opacity: 0.9,
                  whiteSpace: 'nowrap'
                }}>contact</h4>
                <div className="footer-contact" style={{
                  fontSize: isMobile ? '11px' : '14px',
                  lineHeight: '1.6',
                  marginBottom: '10px',
                  opacity: 0.7
                }}>
                  <p style={{ margin: '5px 0', whiteSpace: 'nowrap' }}>
                    email:{' '}
                    <a href="mailto:sam@getthestone.com" style={{
                      color: '#fff',
                      textDecoration: 'none'
                    }}>sam@getthestone.com</a>
                  </p>
                  <p style={{ margin: '5px 0', whiteSpace: 'nowrap' }}>phone: +1 (646) 708-5301</p>
                  <div className="footer-social" style={{
                    display: 'flex',
                    marginTop: '10px'
                  }}>
                    <a
                      href="https://twitter.com/getthestone"
                      rel="noopener noreferrer"
                      target="_blank"
                      style={{
                        marginRight: '15px',
                        opacity: 0.7,
                        color: '#fff',
                        textDecoration: 'none',
                        fontSize: isMobile ? '11px' : '14px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      twitter
                    </a>
                    <a
                      href="https://instagram.com/getthestone"
                      rel="noopener noreferrer"
                      target="_blank"
                      style={{
                        opacity: 0.7,
                        color: '#fff',
                        textDecoration: 'none',
                        fontSize: isMobile ? '11px' : '14px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      instagram
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="footer-bottom" style={{
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              marginTop: '12px',
              paddingTop: '8px',
              paddingBottom: '5px',
              textAlign: 'center',
              width: '100%',
              maxWidth: '1200px'
            }}>
              <p style={{
                fontSize: '12px',
                opacity: 0.5,
                margin: 0
              }}>© 2025 pebble. all rights reserved.</p>
            </div>
          </footer>
        </div>
      )}

      {/* Back navigation (bottom left) - hidden on LANDING frames and footer */}
      {displaySection >= 2 && displaySection <= 13 && (
        <div
          style={{
            ...navButtonBaseStyle,
            left: LAYOUT.spacing.small,
            gap: '6px',
            fontStyle: 'normal',
            color: (displaySection >= 9 && displaySection <= 11) ? COLORS.white : COLORS.white
          }}
          {...createHoverHandlers(!(displaySection >= 9 && displaySection <= 11))}
          onClick={() => {
            // Set progress states FIRST, then section states to prevent glitch
            if (currentSection >= 2 && currentSection <= 8) {
              // From DEMO → go to LANDING (Frame 1)
              // Hide phone SYNCHRONOUSLY to prevent glitch
              flushSync(() => {
                setHidePhone(true);
              });

              setFrame1TextProgress(0);
              setFrame2TextProgress(0);
              setRockProgress(0);
              setFrame3Progress(0);
              setTypewriterProgress(0);
              setScrollIndicatorOpacity(1);

              // Reset all transition progress
              resetAllTransitionProgress();

              // Set sections AFTER progress states
              setCurrentSection(0);
              setDisplaySection(0);
              setIsAnimating(false);

              // Unhide phone after states have settled
              setTimeout(() => setHidePhone(false), 50);
            } else if (currentSection === 9) {
              // From INTERFACE → go to DEMO (Frame 3h)
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);

              setCurrentSection(8);
              setDisplaySection(8);
              setIsAnimating(false);
            } else if (currentSection >= 10 && currentSection <= 12) {
              // From APPROACH → go to INTERFACE (section 9) with slide-down animation
              if (!isAnimating) {
                setIsAnimating(true);
                setDisplaySection(9);
                const startTime = Date.now();
                const duration = ANIMATION_DURATIONS.default;

                const animate = () => {
                  const elapsed = Date.now() - startTime;
                  const progress = Math.min(elapsed / duration, 1);
                  const easedProgress = easeInOutCubic(progress);

                  // Animate backwards by inverting progress
                  setSection10Progress(1 - easedProgress);

                  if (progress < 1) {
                    requestAnimationFrame(animate);
                  } else {
                    setCurrentSection(9);
                    setDisplaySection(9);
                    setIsAnimating(false);
                    setFrame1TextProgress(1);
                    setFrame2TextProgress(1);
                    setRockProgress(1);
                    setFrame3Progress(1);
                    setTypewriterProgress(1);
                    resetAllTransitionProgress();
                  }
                };

                requestAnimationFrame(animate);
              }
            } else if (currentSection === 13) {
              // From WHY → go to APPROACH section 3 (Frame 4c, section 12)
              setFrame1TextProgress(1);
              setFrame2TextProgress(1);
              setRockProgress(1);
              setFrame3Progress(1);
              setTypewriterProgress(1);

              // Reset all transition progress
              resetAllTransitionProgress();

              // Set accordion to section 3 (expanded) immediately without animation
              setPresetAccordionSection(12);
              setSkipAccordionTransition(true);

              // Set sections AFTER progress states - go to section 12 (accordion item 3)
              setCurrentSection(12);
              setDisplaySection(12);
              setIsAnimating(false);

              // Clear preset after a brief delay
              setTimeout(() => {
                setPresetAccordionSection(null);
                setSkipAccordionTransition(false);
              }, 50);
            }
          }}
        >
          <ArrowSVG rotation={180} />
          <span>
            {displaySection >= 2 && displaySection <= 8 ? 'LANDING' :
             displaySection === 9 ? 'DEMO' :
             displaySection >= 10 && displaySection <= 12 ? 'INTERFACE' :
             displaySection === 13 ? 'APPROACH' :
             ''}
          </span>
        </div>
      )}

      {/* DEMO button (bottom right, Frame 2 only - "meet the stone") */}
      {displaySection === 1 && (
        <div
          style={{ ...navButtonBaseStyle, right: LAYOUT.spacing.small }}
          {...createHoverHandlers()}
          onClick={() => {
            // Instant jump to Frame 3a (section 2)
            setCurrentSection(2);
            setDisplaySection(2);
            setIsAnimating(false);

            // Set frame progress states for Frame 3a
            setFrame1TextProgress(1);
            setFrame2TextProgress(1);
            setRockProgress(1);
            setFrame3Progress(1);
            setTypewriterProgress(1);

            // Reset scroll indicator opacity when navigating away from frames 0-1
            setScrollIndicatorOpacity(0);

            // Reset all transition progress states
            resetAllTransitionProgress();
          }}
        >
          <span>DEMO</span>
          <ArrowSVG />
        </div>
      )}

      {/* INTERFACE button for DEMO frames (3a-3h) */}
      {displaySection >= 2 && displaySection <= 8 && (
        <div
          style={{ ...navButtonBaseStyle, right: LAYOUT.spacing.small, zIndex: LAYOUT.zIndex.navigation }}
          {...createHoverHandlers()}
          onClick={() => {
            // Trigger slide-up animation to INTERFACE
            if (!isAnimating) {
              setIsAnimating(true);
              setDisplaySection(9);
              const startTime = Date.now();
              const duration = ANIMATION_DURATIONS.default;

              const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easedProgress = easeInOutCubic(progress);

                setSection9Progress(easedProgress);

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  setCurrentSection(9);
                  setDisplaySection(9);
                  setIsAnimating(false);
                  setFrame1TextProgress(1);
                  setFrame2TextProgress(1);
                  setRockProgress(1);
                  setFrame3Progress(1);
                  setTypewriterProgress(1);
                  setSection3Progress(1);
                  setSection4Progress(1);
                  setSection5Progress(1);
                  setSection6Progress(1);
                  setSection7Progress(1);
                  setSection8Progress(1);
                  setSection9Progress(0);
                  setSection10Progress(0);
                  setSection11Progress(0);
                  setSection12Progress(0);
                  setSection13Progress(0);
                  setSection14Progress(0);
                  setSection15Progress(0);
                }
              };

              requestAnimationFrame(animate);
            }
          }}
        >
          <span>INTERFACE</span>
          <ArrowSVG />
        </div>
      )}

      {/* APPROACH button for INTERFACE frame (section 9) */}
      {displaySection === 9 && (
        <div
          style={{ ...navButtonBaseStyle, right: LAYOUT.spacing.small, zIndex: LAYOUT.zIndex.navigation }}
          {...createHoverHandlers()}
          onClick={() => {
            // Trigger blue slide-up animation to APPROACH
            if (!isAnimating) {
              // Set accordion to expanded state before starting animation
              setPresetAccordionSection(10);
              setSkipAccordionTransition(true);

              setIsAnimating(true);
              setDisplaySection(10); // Update button display immediately - causes APPROACH to render
              const startTime = Date.now();
              const duration = ANIMATION_DURATIONS.default;

              const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easedProgress = easeInOutCubic(progress);

                setSection11Progress(easedProgress);

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  setCurrentSection(10);
                  setDisplaySection(10);
                  setIsAnimating(false);
                  setFrame1TextProgress(1);
                  setFrame2TextProgress(1);
                  setRockProgress(1);
                  setFrame3Progress(1);
                  setTypewriterProgress(1);
                  setSection3Progress(1);
                  setSection4Progress(1);
                  setSection5Progress(1);
                  setSection6Progress(1);
                  setSection7Progress(1);
                  setSection8Progress(1);
                  setSection9Progress(1);
                  setSection10Progress(0);
                  setSection11Progress(0);
                  setSection12Progress(0);
                  setSection13Progress(0);
                  setSection14Progress(0);
                  setSection15Progress(0);
                  setPresetAccordionSection(null);
                  setTimeout(() => setSkipAccordionTransition(false), 50);
                }
              };

              requestAnimationFrame(animate);
            }
          }}
        >
          <span>APPROACH</span>
          <ArrowSVG />
        </div>
      )}

      {/* Scroll to Explore (bottom center, Frame 1 only) */}
      {((currentSection === 0 || currentSection === 1) && scrollIndicatorOpacity > 0) && (
        <div style={{
          position: 'fixed',
          bottom: LAYOUT.spacing.small,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: LAYOUT.zIndex.base,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          fontFamily: TYPOGRAPHY.fontFamily,
          fontSize: TYPOGRAPHY.fontSize.small,
          color: COLORS.white,
          pointerEvents: 'none',
          opacity: scrollIndicatorOpacity
        }}>
          <span>SCROLL TO EXPLORE</span>
          <ArrowSVG />
        </div>
      )}

      {/* Vertical navigation dots (desktop only) */}
      <div style={{
        position: 'fixed',
        left: '40px',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 150,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0px',
        padding: '20px'
      }}
      className="desktop-only-nav"
      onMouseEnter={() => setNavHovered(true)}
      onMouseLeave={() => setNavHovered(false)}
      >
        {/* Navigation dots with line segments between them */}
        {[
          { section: 0, label: '1', range: [0, 1], hoverLabel: 'LANDING' }, // Frames 1-2
          { section: 2, label: '3a', range: [2, 8], hoverLabel: 'DEMO' }, // Frames 3a-3h
          { section: 9, label: '3h', range: [9, 9], hoverLabel: 'INTERFACE' }, // Frame 3h (UI showcase)
          { section: 10, label: '4a', range: [10, 12], hoverLabel: 'APPROACH' }, // Frames 4a-4c
          { section: 13, label: '5', range: [13, 13], hoverLabel: 'WHY' } // Frame 5
        ].map((item, index, array) => {
          // Simplified logic - just check current section range
          const isActive = currentSection >= item.range[0] && currentSection <= item.range[1];
          const isLastDot = index === array.length - 1;

          return (
            <div key={item.section} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {/* Dot with hover label - expanded hover zone */}
              <div
                className="nav-dot-wrapper"
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '100px', // Ensure hover area extends to the right
                  paddingRight: '20px', // Extra padding for easier hovering
                  overflow: 'hidden' // Prevent text from overlapping dots/lines
                }}
                onMouseEnter={() => setHoveredNavDot(item.section)}
                onMouseLeave={() => setHoveredNavDot(null)}
              >
                <button
                  onClick={() => {
                  // Hide phone SYNCHRONOUSLY to prevent glitch when going to frame 1
                  if (item.section === 0 && currentSection >= 2) {
                    flushSync(() => {
                      setHidePhone(true);
                    });
                  }

                  // Set frame progress states FIRST before changing sections
                  // This prevents the 3D phone glitch
                  if (item.section === 0) {
                    // Frame 1
                    setFrame1TextProgress(0);
                    setFrame2TextProgress(0);
                    setRockProgress(0);
                    setFrame3Progress(0);
                    setTypewriterProgress(0);
                    // Show scroll indicator when navigating to frame 1
                    setScrollIndicatorOpacity(1);
                  } else if (item.section === 2) {
                    // Frame 3a
                    setFrame1TextProgress(1);
                    setFrame2TextProgress(1);
                    setRockProgress(1);
                    setFrame3Progress(1);
                    setTypewriterProgress(1);
                    // Hide scroll indicator when navigating away from frames 0-1
                    setScrollIndicatorOpacity(0);
                  } else if (item.section === 9) {
                    // Frame 3h (INTERFACE)
                    setFrame1TextProgress(1);
                    setFrame2TextProgress(1);
                    setRockProgress(1);
                    setFrame3Progress(1);
                    setTypewriterProgress(1);
                    // Hide scroll indicator when navigating away from frames 0-1
                    setScrollIndicatorOpacity(0);
                  } else if (item.section === 10) {
                    // Frame 4a (APPROACH)
                    setFrame1TextProgress(1);
                    setFrame2TextProgress(1);
                    setRockProgress(1);
                    setFrame3Progress(1);
                    setTypewriterProgress(1);
                    // Hide scroll indicator when navigating away from frames 0-1
                    setScrollIndicatorOpacity(0);
                  } else if (item.section === 13) {
                    // Frame 5 (WHY)
                    setFrame1TextProgress(1);
                    setFrame2TextProgress(1);
                    setRockProgress(1);
                    setFrame3Progress(1);
                    setTypewriterProgress(1);
                    // Hide scroll indicator when navigating away from frames 0-1
                    setScrollIndicatorOpacity(0);
                  }

                  // Always reset all transition progress states
                  setSection3Progress(0);
                  setSection4Progress(0);
                  setSection5Progress(0);
                  setSection6Progress(0);
                  setSection7Progress(0);
                  setSection8Progress(0);
                  setSection9Progress(0);
                  setSection10Progress(0);
                  setSection11Progress(0);
                  setSection12Progress(0);
                  setSection13Progress(0);
                  setSection14Progress(0);
                  setSection15Progress(0);

                  // Set section states AFTER progress states to prevent glitch
                  setCurrentSection(item.section);
                  setDisplaySection(item.section);
                  setIsAnimating(false);

                  // Unhide phone after states have settled
                  setTimeout(() => setHidePhone(false), 50);
                }}
                style={{
                  position: 'relative',
                  width: '12px',
                  height: '12px',
                  border: '1px solid #ffffff',
                  borderRadius: '50%',
                  backgroundColor: isActive ? '#ffffff' : (hoveredNavDot === item.section ? '#ffffff' : 'transparent'),
                  cursor: 'pointer',
                  padding: 0,
                  marginTop: index === 0 ? '0' : '0',
                  transition: 'background-color 0.3s ease',
                  zIndex: 2,
                  display: 'block'
                }}
                  aria-label={`Go to frame ${item.label}`}
                />

                {/* Hover label */}
                <button
                  onClick={() => {
                    // Same navigation logic as dot
                    setCurrentSection(item.section);
                    setDisplaySection(item.section);
                    setIsAnimating(false);

                    if (item.section === 0) {
                      setFrame1TextProgress(0);
                      setFrame2TextProgress(0);
                      setRockProgress(0);
                      setFrame3Progress(0);
                      setTypewriterProgress(0);
                      setScrollIndicatorOpacity(1);
                    } else if (item.section === 2) {
                      setFrame1TextProgress(1);
                      setFrame2TextProgress(1);
                      setRockProgress(1);
                      setFrame3Progress(1);
                      setTypewriterProgress(1);
                      setScrollIndicatorOpacity(0);
                    } else if (item.section === 9) {
                      setFrame1TextProgress(1);
                      setFrame2TextProgress(1);
                      setRockProgress(1);
                      setFrame3Progress(1);
                      setTypewriterProgress(1);
                      setScrollIndicatorOpacity(0);
                    } else if (item.section === 10) {
                      setFrame1TextProgress(1);
                      setFrame2TextProgress(1);
                      setRockProgress(1);
                      setFrame3Progress(1);
                      setTypewriterProgress(1);
                      setScrollIndicatorOpacity(0);
                    } else if (item.section === 13) {
                      setFrame1TextProgress(1);
                      setFrame2TextProgress(1);
                      setRockProgress(1);
                      setFrame3Progress(1);
                      setTypewriterProgress(1);
                      setScrollIndicatorOpacity(0);
                    }

                    setSection3Progress(0);
                    setSection4Progress(0);
                    setSection5Progress(0);
                    setSection6Progress(0);
                    setSection7Progress(0);
                    setSection8Progress(0);
                    setSection9Progress(0);
                    setSection10Progress(0);
                    setSection11Progress(0);
                    setSection12Progress(0);
                    setSection13Progress(0);
                    setSection14Progress(0);
                    setSection15Progress(0);
                  }}
                  className="nav-hover-label"
                  style={{
                    position: 'absolute',
                    left: '20px', // Position to the right of the dot (12px dot + 8px spacing)
                    color: '#ffffff',
                    whiteSpace: 'nowrap',
                    opacity: navHovered ? 1 : 0,
                    transform: navHovered ? 'translateX(0)' : 'translateX(-10px)', // Reduced slide distance
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                    fontSize: '10pt',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 400,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.fontWeight = '500';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.fontWeight = '400';
                  }}
                >
                  {item.hoverLabel}
                </button>
              </div>

              {/* Line segment connecting to next dot (except for last dot) */}
              {!isLastDot && (
                <div style={{
                  width: '1px',
                  height: '40px',
                  backgroundColor: '#ffffff',
                  marginTop: '0',
                  marginLeft: '6px', // Center align with dot (dot is 12px wide, so 12/2 = 6px)
                  zIndex: 0
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Add desktop-only media query and responsive styles */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 0.6;
              width: 70%;
            }
            50% {
              opacity: 1;
              width: 90%;
            }
          }

          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: translateY(10px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeInUp {
            0% {
              opacity: 0;
              transform: translate(-50%, 10px);
            }
            100% {
              opacity: 1;
              transform: translate(-50%, 0);
            }
          }

          /* Sliding text animation for cycling adjectives */
          .adjective-exit {
            animation: slideUpExit 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }

          .adjective-enter {
            animation: slideUpEnter 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }

          @keyframes slideUpExit {
            0% {
              transform: translateY(0);
              opacity: 1;
            }
            100% {
              transform: translateY(-100%);
              opacity: 0;
            }
          }

          @keyframes slideUpEnter {
            0% {
              transform: translateY(100%);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }

          @media (max-width: 768px) {
            .desktop-only-nav {
              display: none !important;
            }
            .frame4-title {
              margin-bottom: 6px !important;
            }
          }
        `}
      </style>
    </div>
    </>
  );
}
